/**
 * @fileoverview New Job Contact dialog
 *
 * Purpose: lets a practitioner manually add a contact to a job's CRM. The
 * reconcile pipeline normally owns `jobContacts`, but some parties are only
 * mentioned in correspondence and never surface in an agent finding — this
 * form inserts a row with no source links (reconcile leaves such rows alone).
 *
 * Fields mirror the editable overlay in the contact detail panel: name,
 * contact type, email/phone, ABN/ACN, structured addresses (via the shared
 * `AddressesEditor`), and roles. Date of birth and firm links are left to the
 * detail panel, which has the dedicated date picker / link picker.
 *
 * Gated to job writers (owner/member) by the caller; the backend also enforces
 * write access via `verifyJobWriteAccess`.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Check, Info, Loader2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Badge } from "@/ui/primitives/badge";
import { Alert, AlertDescription } from "@/ui/primitives/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/composed/overlays/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";
import { CommandSeparator } from "@/ui/composed/menus/command";
import { AddressesEditor } from "./AddressesEditor";
import {
  CONTACT_TYPE_META,
  CONTACT_TYPE_ORDER,
  ROLE_META,
  ROLE_ORDER,
  resolveCustomRoleDisplays,
  type ContactAddress,
  type ContactRole,
  type ContactType,
  type CustomJobRole,
} from "./types";

interface NewJobContactDialogProps {
  jobId: Id<"jobs">;
  orgId: Id<"organizations">;
}

export function NewJobContactDialog({
  jobId,
  orgId,
}: NewJobContactDialogProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [contactType, setContactType] = useState<ContactType>("person");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [abn, setAbn] = useState("");
  const [acn, setAcn] = useState("");
  const [addresses, setAddresses] = useState<Array<ContactAddress>>([]);
  const [roles, setRoles] = useState<Set<ContactRole>>(() => new Set());
  const [customRoleIds, setCustomRoleIds] = useState<
    Set<Id<"jobContactRoles">>
  >(() => new Set());

  const createContact = useConvexMutation(
    api.contacts.mutations.createJobContact,
  );

  function reset() {
    setDisplayName("");
    setContactType("person");
    setEmail("");
    setPhone("");
    setAbn("");
    setAcn("");
    setAddresses([]);
    setRoles(new Set());
    setCustomRoleIds(new Set());
  }

  async function handleSubmit() {
    if (!displayName.trim()) {
      toast.error("Name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await createContact({
        jobId,
        input: {
          displayName: displayName.trim(),
          contactType,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          abn: abn.replace(/\s/g, "") || undefined,
          acn: acn.replace(/\s/g, "") || undefined,
          // Drop empty addresses (a row with nothing but the default label).
          addresses: addresses.filter(hasAddressContent),
          roles: ROLE_ORDER.filter((r) => roles.has(r)),
          customRoleIds: Array.from(customRoleIds),
        },
      });
      toast.success("Contact added.");
      reset();
      setOpen(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : undefined;
      toast.error(message || "Could not add contact.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // State is intentionally preserved when the dialog is dismissed without
    // submitting; it only resets after a successful create.
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-2">
          <Plus className="h-4 w-4" />
          Add Contact
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Contact</DialogTitle>
          <DialogDescription>
            Manually add a contact to this job.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Contacts added manually are <strong>not analysed</strong> by the
            data agents. If you want this individual or entity to be included in
            the analysis, add them directly in the relevant agent (e.g. add a
            creditor in the Creditors agent) for it to be analysed and captured
            as a job contact.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Name *">
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jane Smith, Acme Pty Ltd, …"
                autoFocus
              />
            </FieldRow>
            <FieldRow label="Contact type">
              <Select
                value={contactType}
                onValueChange={(v) => setContactType(v as ContactType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPE_ORDER.map((t) => {
                    const meta = CONTACT_TYPE_META[t];
                    return (
                      <SelectItem key={t} value={t}>
                        <span className="inline-flex items-center gap-2">
                          <meta.icon className="h-3.5 w-3.5" />
                          {meta.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FieldRow>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@example.com"
              />
            </FieldRow>
            <FieldRow label="Phone">
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+61 …"
              />
            </FieldRow>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="ABN">
              <Input
                value={abn}
                onChange={(e) => setAbn(e.target.value)}
                placeholder="11 digits"
                className="font-mono"
              />
            </FieldRow>
            <FieldRow label="ACN">
              <Input
                value={acn}
                onChange={(e) => setAcn(e.target.value)}
                placeholder="9 digits"
                className="font-mono"
              />
            </FieldRow>
          </div>

          <FieldRow label="Roles">
            <RolesPicker
              orgId={orgId}
              selected={roles}
              onChange={setRoles}
              selectedCustom={customRoleIds}
              onChangeCustom={setCustomRoleIds}
            />
          </FieldRow>

          <FieldRow label="Addresses">
            <AddressesEditor addresses={addresses} onChange={setAddresses} />
          </FieldRow>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding…
              </>
            ) : (
              "Add contact"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** True when an address row carries any field beyond the default label. */
function hasAddressContent(address: ContactAddress): boolean {
  return Boolean(
    address.line1?.trim() ||
    address.line2?.trim() ||
    address.suburb?.trim() ||
    address.state?.trim() ||
    address.postcode?.trim(),
  );
}

/** Default badge colour for a role created inline from this picker. */
const DEFAULT_CUSTOM_ROLE_COLOR = "blue" as const;

/**
 * Inline multi-select for contact roles, staged locally so the create dialog
 * can pass the chosen set straight into `createJobContact`. Mirrors the
 * command-popover pattern used by `ContactRolesEditor` for post-create edits,
 * including org-defined custom roles with an inline "create" affordance.
 */
function RolesPicker({
  orgId,
  selected,
  onChange,
  selectedCustom,
  onChangeCustom,
}: {
  orgId: Id<"organizations">;
  selected: Set<ContactRole>;
  onChange: (next: Set<ContactRole>) => void;
  selectedCustom: Set<Id<"jobContactRoles">>;
  onChangeCustom: (next: Set<Id<"jobContactRoles">>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const { data: customRoles } = useQuery(
    convexQuery(api.contacts.customRoles.listJobContactRoles, { orgId }),
  );
  const customById = useMemo(() => {
    const map = new Map<string, CustomJobRole>();
    for (const role of customRoles ?? []) map.set(role._id, role);
    return map;
  }, [customRoles]);

  const createRole = useConvexMutation(
    api.contacts.customRoles.createJobContactRole,
  );

  const currentRoles = useMemo(
    () => ROLE_ORDER.filter((r) => selected.has(r)),
    [selected],
  );
  const currentCustom = useMemo(
    () => resolveCustomRoleDisplays(Array.from(selectedCustom), customById),
    [selectedCustom, customById],
  );

  const toggle = (role: ContactRole) => {
    const next = new Set(selected);
    if (next.has(role)) next.delete(role);
    else next.add(role);
    onChange(next);
  };

  const toggleCustom = (roleId: Id<"jobContactRoles">) => {
    const next = new Set(selectedCustom);
    if (next.has(roleId)) next.delete(roleId);
    else next.add(roleId);
    onChangeCustom(next);
  };

  const trimmedQuery = query.trim();
  const canCreate =
    trimmedQuery.length > 0 &&
    !(customRoles ?? []).some(
      (r) => r.name.toLowerCase() === trimmedQuery.toLowerCase(),
    );

  const handleCreate = async () => {
    if (!canCreate || isCreating) return;
    setIsCreating(true);
    try {
      const roleId = await createRole({
        orgId,
        name: trimmedQuery,
        color: DEFAULT_CUSTOM_ROLE_COLOR,
      });
      onChangeCustom(new Set(selectedCustom).add(roleId));
      setQuery("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create role");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {currentRoles.map((role) => {
        const meta = ROLE_META[role];
        return (
          <Badge key={role} color={meta.badgeColor} variant="soft" size="sm">
            {meta.label}
          </Badge>
        );
      })}
      {currentCustom.map((display) => (
        <Badge
          key={display.key}
          color={display.badgeColor}
          variant="soft"
          size="sm"
        >
          {display.label}
        </Badge>
      ))}
      {currentRoles.length === 0 && currentCustom.length === 0 && (
        <span className="text-xs text-muted-foreground/60">No roles</span>
      )}

      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <Pencil className="h-3 w-3" />
            Edit roles
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command
            filter={(value, search) => {
              const q = search.trim().toLowerCase();
              if (!q) return 1;
              return value.toLowerCase().includes(q) ? 1 : 0;
            }}
          >
            <CommandInput
              placeholder="Search or create a role…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList className="max-h-[280px] overscroll-contain">
              <CommandEmpty>No matching roles.</CommandEmpty>
              <CommandGroup heading="Built-in">
                {ROLE_ORDER.map((role) => {
                  const meta = ROLE_META[role];
                  const isSelected = selected.has(role);
                  return (
                    <CommandItem
                      key={role}
                      value={meta.label}
                      onSelect={() => toggle(role)}
                      className="gap-2"
                    >
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded border",
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-input",
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <span className="text-sm">{meta.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {(customRoles ?? []).length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Custom">
                    {(customRoles ?? []).map((role) => {
                      const isSelected = selectedCustom.has(role._id);
                      return (
                        <CommandItem
                          key={role._id}
                          value={role.name}
                          onSelect={() => toggleCustom(role._id)}
                          className="gap-2"
                        >
                          <div
                            className={cn(
                              "flex h-4 w-4 items-center justify-center rounded border",
                              isSelected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-input",
                            )}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </div>
                          <span className="text-sm">{role.name}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
              {canCreate && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      value={`__create__${trimmedQuery}`}
                      onSelect={handleCreate}
                      disabled={isCreating}
                      className="gap-2"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span className="text-sm">Create “{trimmedQuery}”</span>
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}
